/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { MultiRegionMigrationModal } from 'src/verticals/azureMigrationModal/MultiRegionMigrationModal';

const messages = defineMessages({
    bodyMessage: {
        id: 'luis.migrationBlockerPage.bodyMessage',
        defaultMessage: 'To continue using the LUIS portal, please migrate your apps to an Azure authoring resource.'
    },
    migrate: {
        id: 'luis.migrationBlockerPage.migrate',
        defaultMessage: 'Migrate to Azure'
    },
    error: {
        id: 'luis.migrationBlockerPage.error',
        defaultMessage: `An error occurred while fetching your information. Please refresh the page
to try again or contact support if the error persists.`
    }
});

const theme = getScopedTheme('MigrationBlockerPage');

export const MigrationBlockerPage = () => {
    const [isModalHidden, setIsModalHidden] = React.useState(false);
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const migrationInfoTracker = rootStore.userMigrationInfoStoreHouse.tracker;

    return (
        <>
            <Stack verticalFill verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: 20 }}>
                <LuisText>
                    {localize(messages.bodyMessage)}
                    &nbsp;
                    <ExternalLink href="https://docs.microsoft.com/azure/cognitive-services/luis/luis-migration-authoring">
                        {localize(globalMessages.learnMore)}
                    </ExternalLink>
                </LuisText>

                {migrationInfoTracker.isFailed ? (
                    <LuisText color={theme.errorColor} fontWeight={500}>
                        {localize(messages.error)}
                    </LuisText>
                ) : (
                    <PrimaryButton onClick={() => setIsModalHidden(false)}>{localize(messages.migrate)}</PrimaryButton>
                )}
            </Stack>

            {!migrationInfoTracker.isFailed && (
                <MultiRegionMigrationModal hidden={isModalHidden} onDismiss={() => setIsModalHidden(true)} />
            )}
        </>
    );
};
